import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface NewMessageModalProps {
  open: boolean;
  onClose: () => void;
}

interface CreateThreadRequest {
  subject: string;
  category: string;
  content: string;
  priority: string;
}

interface CreateThreadResponse {
  success: boolean;
  data?: {
    threadId: number;
    messageId: number;
  };
  error?: string;
  message?: string;
}

const NewMessageModal: React.FC<NewMessageModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('normal');
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<{
    subject?: string;
    content?: string;
  }>({});

  const createThreadMutation = useMutation({
    mutationFn: async (data: CreateThreadRequest) => {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/messages/threads`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }
      );

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.message || 'Failed to create message');
      }

      const responseData: CreateThreadResponse = await response.json();
      return responseData;
    },
    onSuccess: (data) => {
      if (data.data?.threadId) {
        // Navigate to the new thread
        navigate(`/messages/${data.data.threadId}`);
        handleClose();
      }
    },
    onError: (error: Error) => {
      setError(error.message || 'Failed to send message. Please try again.');
      setIsSubmitting(false);
    },
  });

  const handleClose = () => {
    // Reset form
    setSubject('');
    setCategory('general');
    setPriority('normal');
    setContent('');
    setError(null);
    setValidationErrors({});
    setIsSubmitting(false);
    onClose();
  };

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setCategory(event.target.value);
  };

  const handlePriorityChange = (event: SelectChangeEvent) => {
    setPriority(event.target.value);
  };

  const validateForm = () => {
    const errors: { subject?: string; content?: string } = {};
    
    if (!subject.trim()) {
      errors.subject = 'Subject is required';
    } else if (subject.trim().length < 3) {
      errors.subject = 'Subject must be at least 3 characters';
    } else if (subject.length > 255) {
      errors.subject = 'Subject must be less than 255 characters';
    }
    
    if (!content.trim()) {
      errors.content = 'Message content is required';
    } else if (content.trim().length < 10) {
      errors.content = 'Message must be at least 10 characters';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    setError(null);
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    const data: CreateThreadRequest = {
      subject: subject.trim(),
      category,
      content: content.trim(),
      priority,
    };

    createThreadMutation.mutate(data);
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>New Message</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="Subject"
            fullWidth
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            error={!!validationErrors.subject}
            helperText={validationErrors.subject}
            disabled={isSubmitting}
            placeholder="Brief description of your inquiry"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                label="Category"
                onChange={handleCategoryChange}
                disabled={isSubmitting}
              >
                <MenuItem value="general">General</MenuItem>
                <MenuItem value="support">Support</MenuItem>
                <MenuItem value="billing">Billing</MenuItem>
                <MenuItem value="technical">Technical</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={priority}
                label="Priority"
                onChange={handlePriorityChange}
                disabled={isSubmitting}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TextField
            label="Message"
            fullWidth
            required
            multiline
            rows={6}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            error={!!validationErrors.content}
            helperText={validationErrors.content || 'Please provide details about your inquiry'}
            disabled={isSubmitting}
            placeholder="Please describe your question or issue in detail..."
          />

          <Typography variant="caption" color="text.secondary">
            Our support team typically responds within 24 hours during business days.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleClose} 
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={isSubmitting || !subject.trim() || !content.trim()}
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewMessageModal;